import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Stack,
  ListItem,
  TextField,
  Typography,
  Autocomplete,
  InputAdornment,
  CircularProgress
} from "@material-ui/core";
import parseSubstrings from "autosuggest-highlight/parse";
import { useTranslation } from "react-i18next";
import { Search } from "@material-ui/icons";
import { LocationOn } from "@material-ui/icons";
import { debounce } from "lodash";
import { textIncludes } from "utils/table";
import type { SyntheticEvent } from "react";
import type { CreateAddressDetails } from "components/_dashboard/listingsMatch/CreateAddressDialog";

const autocompleteService: {
  current: google.maps.places.AutocompleteService | null;
} = { current: null };

const placesService: {
  current: google.maps.places.PlacesService | null;
} = { current: null };

type AutoCompletePrediction = google.maps.places.AutocompletePrediction;

export type PlaceAutoCompleteProps = {
  type?: keyof PlaceTypes;
  required?: boolean;
  disabled?: boolean;
  inputValue: string;
  handleInputChange: (_event: SyntheticEvent, newInputValue: string) => void;
  handleAutoCompleteFill?: (data: Partial<CreateAddressDetails>) => void;
};

export function PlaceAutoComplete({
  type,
  disabled,
  required,
  inputValue,
  handleInputChange,
  handleAutoCompleteFill
}: PlaceAutoCompleteProps) {
  const [value, setValue] = useState<AutoCompletePrediction | null>(null);
  const [options, setOptions] = useState<AutoCompletePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken | null>(null);

  const { t: translate } = useTranslation();

  const fetch = useMemo(
    () =>
      debounce<google.maps.places.AutocompleteService["getPlacePredictions"]>(
        (request, callback) => autocompleteService.current.getPlacePredictions(request, callback),
        400
      ),
    []
  );

  useEffect(() => {
    let active = true;

    if (window.google) {
      if (!autocompleteService.current) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }

      if (!placesService.current) {
        placesService.current = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );
      }
    }

    if (!autocompleteService.current || !placesService.current) return;

    if (!sessionToken) {
      const newSessionToken = new google.maps.places.AutocompleteSessionToken();
      setSessionToken(newSessionToken);
      return;
    }

    if (!inputValue) return;

    setLoading(true);

    let searchTypes: string[] | null = null;

    if (type) searchTypes = placeTypes[type];

    fetch(
      {
        input: inputValue,
        types: searchTypes,
        sessionToken,
        language: "iw"
      },
      (results): void => {
        const shouldSetOptions = active && results;

        if (shouldSetOptions) setOptions(results);

        setLoading(false);
      }
    );
    return () => {
      active = false;
    };
  }, [value, sessionToken, type, inputValue, fetch]);

  useEffect(() => {
    if (!handleAutoCompleteFill) return;

    if (!value) return;

    const typesCanBeFilled = ["address", "neighborhood"].includes(type);

    if (!typesCanBeFilled) return;

    const getAddressComponentsFromPlaceId = (
      placeId: string
    ): Promise<google.maps.GeocoderAddressComponent[] | null> => {
      return new Promise((resolve) => {
        placesService.current?.getDetails(
          { placeId, sessionToken, language: "iw" },
          (place, status) => {
            setSessionToken(null);

            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(place?.address_components ?? []);
            } else {
              console.error(`Failed to get place details: ${status}`);
              resolve(null);
            }
          }
        );
      });
    };

    const fillAddressDetails = async (): Promise<void> => {
      const addressComponents = await getAddressComponentsFromPlaceId(value.place_id);

      if (!addressComponents?.length) return;

      const newAddressForm: CreateAddressDetails = {
        address: "",
        neighborhood: "",
        city: ""
      };

      const splittedDescription = value.description.trim().split(" ").filter(Boolean);

      for (const { types, short_name, long_name } of addressComponents) {
        const splittedShortName = short_name.split(" ").filter(Boolean);
        const splittedLongName = long_name.split(" ").filter(Boolean);

        const matchAddressComponent = splittedDescription.some((wordPart) => {
          const matchShortName = splittedShortName.some((shortNamePart) =>
            textIncludes(wordPart, shortNamePart)
          );

          const matchLongName = splittedLongName.some((longNamePart) =>
            textIncludes(wordPart, longNamePart)
          );

          return matchShortName || matchLongName;
        });

        if (!matchAddressComponent) continue;

        const canFillNeighborhood =
          !newAddressForm.neighborhood &&
          placeTypes.neighborhood.some((neighborhoodType) => types.includes(neighborhoodType));

        const canFillCity =
          !newAddressForm.city && placeTypes.city.some((cityType) => types.includes(cityType));

        if (type === "address") {
          if (canFillNeighborhood) {
            newAddressForm.neighborhood = long_name;
            continue;
          }

          if (canFillCity) {
            newAddressForm.city = long_name;
            continue;
          }
        }

        if (type === "neighborhood") {
          if (canFillCity) {
            newAddressForm.city = long_name;
            continue;
          }
        }
      }

      const { neighborhood, city } = newAddressForm;

      const canFillAddress = type === "address" && (neighborhood || city);

      if (canFillAddress) {
        const parsedAddressForm: Partial<CreateAddressDetails> = {};

        if (neighborhood) parsedAddressForm.neighborhood = neighborhood;
        if (city) parsedAddressForm.city = city;

        handleAutoCompleteFill(parsedAddressForm);
      }

      const canFillNeighborhood = type === "neighborhood" && city;

      if (canFillNeighborhood) {
        handleAutoCompleteFill({ city });
      }
    };

    void fillAddressDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, type]);

  const handleChangeOptions = (
    _event: SyntheticEvent,
    newValue: AutoCompletePrediction | null
  ): void => {
    setValue(newValue);
  };

  const handleOptionsLabel = (option: AutoCompletePrediction): string => {
    if (typeof option === "string") return option;

    if (type) {
      return option.structured_formatting.main_text;
    }

    return option.description;
  };

  return (
    <Autocomplete
      disabled={disabled}
      getOptionLabel={handleOptionsLabel}
      filterOptions={(x) => x}
      options={options}
      disableClearable
      includeInputInList
      filterSelectedOptions
      freeSolo
      value={inputValue}
      noOptionsText={translate("listingsPage.noLocations")}
      fullWidth
      onChange={handleChangeOptions}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          label={type ? translate(`listingsPage.tableColumns.${type}`) : undefined}
          inputProps={{
            ...params.inputProps,
            autoComplete: "new-password"
          }}
          InputProps={{
            ...params.InputProps,
            ...(!type && {
              placeholder: translate("general.search"),
              startAdornment: (
                <InputAdornment sx={{ mx: 0.5 }} position="start">
                  <Search />
                </InputAdornment>
              )
            }),
            endAdornment: loading && <CircularProgress size={20} />
          }}
          required={required}
        />
      )}
      renderOption={(
        props,
        { structured_formatting: { main_text, secondary_text, main_text_matched_substrings } }
      ) => {
        const matchedSubstrings = main_text_matched_substrings ?? [];

        type textPart = {
          text: string;
          highlight: boolean;
        };

        const textParts: textPart[] = parseSubstrings(
          main_text,
          matchedSubstrings.map(({ offset, length }) => [offset, offset + length])
        );

        return (
          <ListItem {...props}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <LocationOn sx={{ color: "text.secondary" }} />
              <Box sx={{ wordWrap: "break-word" }}>
                {textParts.map(({ text, highlight }, index) => (
                  <Typography
                    variant="body1"
                    component="span"
                    color="text.primary"
                    fontWeight={highlight ? "bold" : "regular"}
                    key={index}
                  >
                    {text}
                  </Typography>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {secondary_text}
                </Typography>
              </Box>
            </Stack>
          </ListItem>
        );
      }}
    />
  );
}

export type PlaceAutoCompleteIds = "address" | "neighborhood" | "city";

export type PlaceTypes = Record<PlaceAutoCompleteIds, string[]>;

const placeTypes: PlaceTypes = {
  address: ["address"],
  neighborhood: ["neighborhood", "administrative_area_level_4", "administrative_area_level_5"],
  city: ["locality", "administrative_area_level_1", "administrative_area_level_2"]
};
